<template>
  <div class="survey-image-layout-settings">
    <div class="mb-4">
      <h6 class="mb-3 text-sm font-normal text-gray-700">{{ t("common.imageLayoutSettings") }}</h6>

      <!-- Mode Selection -->
      <div class="form-group">
        <div class="flex space-x-4">
          <label class="flex items-center">
            <input
              v-model="imageLayoutMode"
              type="radio"
              :value="SURVEY_MODE.SURVEY_DEFAULT"
              class="mr-2"
              @change="handleModeChange"
            />
            {{ t("common.surveyDefault") }}
          </label>
          <label class="flex items-center">
            <input
              v-model="imageLayoutMode"
              type="radio"
              :value="SURVEY_MODE.INDEPENDENT"
              class="mr-2"
              @change="handleModeChange"
            />
            {{ t("common.independent") }}
          </label>
        </div>
      </div>

      <!-- Independent Settings (per slide) -->
      <div v-if="imageLayoutMode === SURVEY_MODE.INDEPENDENT">
        <div class="mb-4">
          <ImageLayoutSettings
            :show-image="currentSlideSettings.showImage"
            :desktop-image-layout="currentSlideSettings.desktopImageLayout"
            :mobile-image-layout="currentSlideSettings.mobileImageLayout"
            :header-image-src="currentSlideSettings.headerImageSrc"
            :mobile-header-image-src="currentSlideSettings.mobileHeaderImageSrc"
            :image-field-spacing="currentSlideSettings.imageFieldSpacing"
            :image-corner-radius="currentSlideSettings.imageCornerRadius"
            :is-survey="true"
            :selected-slide-index="props.selectedSlideIndex"
            :is-editing-survey-default="false"
            @update:show-image="updateCurrentSlideShowImage"
            @update:desktop-image-layout="updateCurrentSlideDesktopLayout"
            @update:mobile-image-layout="updateCurrentSlideMobileLayout"
            @update:header-image-src="updateCurrentSlideHeaderImage"
            @update:mobile-header-image-src="updateCurrentSlideMobileHeaderImage"
            @update:image-field-spacing="updateCurrentSlideImageFieldSpacing"
            @update:image-corner-radius="updateCurrentSlideImageCornerRadius"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { IMAGE_LAYOUT, SURVEY_MODE } from "@/util/constants";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import ImageLayoutSettings from "./ImageLayoutSettings.vue";

const { t } = useI18n();
const store = useAppStore();

const props = defineProps({
  selectedSlideIndex: {
    type: Number,
    required: true,
  },
});

// Helper function to ensure slideSettings exists
const ensureSlideSettings = () => {
  if (!store.surveyImageSettings.slideSettings) {
    store.surveyImageSettings.slideSettings = {};
  }
};

// Computed properties
const imageLayoutMode = computed({
  get: () => {
    const slideId = store.slides[props.selectedSlideIndex]?.id;

    // Check if slideSettings exists without modifying
    return store.surveyImageSettings.slideSettings?.[slideId]?.mode || SURVEY_MODE.SURVEY_DEFAULT;
  },
  set: (value) => {
    const slideId = store.slides[props.selectedSlideIndex]?.id;

    // Ensure slideSettings exists
    ensureSlideSettings();

    if (!store.surveyImageSettings.slideSettings[slideId]) {
      store.surveyImageSettings.slideSettings[slideId] = {
        ...currentSlideSettings.value,
        mode: value,
        showImage: value === SURVEY_MODE.INDEPENDENT ? true : currentSlideSettings.value.showImage,
      };
    } else {
      store.surveyImageSettings.slideSettings[slideId].mode = value;

      // Automatically enable Show Image when switching to Independent mode
      if (value === SURVEY_MODE.INDEPENDENT) {
        store.surveyImageSettings.slideSettings[slideId].showImage = true;
      }
    }

    store.anyUnsavedChanges = true;
  },
});

const currentSlideSettings = computed(() => {
  const slideId = store.slides[props.selectedSlideIndex]?.id;

  // Check if slideSettings exists without modifying
  const slideSettings = store.surveyImageSettings.slideSettings?.[slideId];

  // If no slide settings exist, return defaults
  if (!slideSettings) {
    return {
      mode: SURVEY_MODE.SURVEY_DEFAULT,
      showImage: false,
      desktopImageLayout: IMAGE_LAYOUT.TOP_FIXED,
      mobileImageLayout: IMAGE_LAYOUT.HEADER,
      headerImageSrc: "",
      mobileHeaderImageSrc: "",
      imageFieldSpacing: 24,
      imageCornerRadius: 0,
    };
  }

  // If slide settings exist but showImage is undefined and mode is independent, default to true
  if (slideSettings.mode === "independent" && slideSettings.showImage === undefined) {
    return {
      ...slideSettings,
      showImage: true,
    };
  }

  return slideSettings;
});

const currentSlideName = computed(() => {
  return (
    store.slides[props.selectedSlideIndex]?.slideName || `Slide ${props.selectedSlideIndex + 1}`
  );
});

// Methods
const handleModeChange = () => {
  store.anyUnsavedChanges = true;
};

/**
 * Generic function to update slide settings property
 * @param property - Property name to update
 * @param value - Value to set
 */
const updateCurrentSlideProperty = (property: string, value: unknown) => {
  const slideId = store.slides[props.selectedSlideIndex]?.id;

  // Ensure slideSettings exists
  ensureSlideSettings();

  if (!store.surveyImageSettings.slideSettings[slideId]) {
    store.surveyImageSettings.slideSettings[slideId] = { ...currentSlideSettings.value };
  }
  (store.surveyImageSettings.slideSettings[slideId] as any)[property] = value;
  store.anyUnsavedChanges = true;
};

const updateCurrentSlideShowImage = (value: boolean) => {
  updateCurrentSlideProperty("showImage", value);
};

const updateCurrentSlideDesktopLayout = (value: string) => {
  updateCurrentSlideProperty("desktopImageLayout", value);
};

const updateCurrentSlideMobileLayout = (value: string) => {
  updateCurrentSlideProperty("mobileImageLayout", value);
};

const updateCurrentSlideHeaderImage = (value: string) => {
  updateCurrentSlideProperty("headerImageSrc", value);
};

const updateCurrentSlideMobileHeaderImage = (value: string) => {
  updateCurrentSlideProperty("mobileHeaderImageSrc", value);
};

const updateCurrentSlideImageFieldSpacing = (value: number) => {
  updateCurrentSlideProperty("imageFieldSpacing", value);
};

const updateCurrentSlideImageCornerRadius = (value: number) => {
  updateCurrentSlideProperty("imageCornerRadius", value);
};
</script>

<style scoped>
.survey-image-layout-settings {
  margin-bottom: 1rem;
}

.field-name-sidebar {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-align: center !important;
  margin-bottom: 1rem !important;
}

.form-group {
  margin-bottom: 1rem;
}

.hl-text-input-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}
</style>
